/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { addressFormSchema } from '@/components/blocks/AddressForm/addressFormSchema';
import {
	ADDRESS_FORM_IDS,
	COUNTRY_LABELS,
	INIT,
	OTHER_COLONY,
} from '@/components/blocks/AddressForm/constants';
import { useNoExteriorNumber } from '@/components/blocks/AddressForm/hooks/useNoExteriorNumber';
import { ExteriorNumberField } from '@/components/blocks/AddressForm/parts/ExteriorNumberField';
import { FieldTooltip } from '@/components/blocks/AddressForm/parts/FieldTooltip';
import { NoExteriorNumberCheckbox } from '@/components/blocks/AddressForm/parts/NoExteriorNumberCheckbox';
import {
	AAFieldItemSX,
	AAFormSelectSX,
	AANoExteriorNumberTwoColumnsSX,
} from '@/components/blocks/AddressForm/styles/formFields';
import { AddressErrors } from '@/components/blocks/AddressForm/types';
import {
	onBeforeInputNumeric,
	onDropNumeric,
	onPasteNumeric,
	validateNumericInput,
} from '@/components/blocks/AddressForm/utils';
import { EMPTY_STRING } from '@/data/constants/marketing';
import { REGEX } from '@/data/constants/regex';
import { useLocalization } from '@/data/Localization';
import { EditableAddress } from '@/data/types/Address';
import { ErrorState, FormState } from '@/utils/useForm';
import { ChevronRight } from '@mui/icons-material';
import {
	FormControl,
	FormHelperText,
	Grid,
	InputLabel,
	MenuItem,
	Select,
	SelectChangeEvent,
	Stack,
	SxProps,
	TextField,
	Theme,
} from '@mui/material';
import { ChangeEvent, Dispatch, FC, SetStateAction } from 'react';

type Props = {
	nickNameDisabled: boolean;
	hiddenNicknameField: boolean;
	address: EditableAddress;
	handleInputChange: (event: ChangeEvent<HTMLInputElement>) => void;
	handleSelectChange: (event: SelectChangeEvent) => void;
	handleColonySelectChange: (event: SelectChangeEvent) => void;
	handleMunicipalitySelectChange: (event: SelectChangeEvent) => void;
	handleZipCodeInputChange: (event: ChangeEvent<HTMLInputElement>) => void;
	onNamedValueChange: FormState<EditableAddress>['onNamedValueChange'];
	validateNickName: (nickName: string) => void;
	addressErrors: AddressErrors;
	setAddressErrors: Dispatch<SetStateAction<AddressErrors>>;
	setIsEdited: Dispatch<SetStateAction<boolean>>;
	cleanFieldsPaste: () => void;
	error: ErrorState<EditableAddress>;
	cities: string[];
	colonies: string[];
	states: string[];
	helperText: { [k: string]: string };
	showInputCity: boolean;
	showInputStateAndCity: boolean;
	formLabel?: JSX.Element;
	isTwoColumns?: boolean;
	colonyDisabled?: boolean;
	cityDisabled?: boolean;
	stateDisabled?: boolean;
	countries?: string[];
	countryDisabled?: boolean;
	isMainAddress?: boolean;
	addressType?: 'Billing';
	isNotBilling?: boolean;
	selectMenuSx?: SxProps<Theme>;
};

export const AddressFormFields: FC<Props> = ({
	address,
	handleInputChange,
	handleSelectChange,
	handleColonySelectChange,
	handleMunicipalitySelectChange,
	handleZipCodeInputChange,
	onNamedValueChange,
	validateNickName,
	addressErrors,
	setAddressErrors,
	setIsEdited,
	cleanFieldsPaste,
	error,
	cities,
	colonies,
	states,
	helperText,
	showInputCity,
	showInputStateAndCity,
	nickNameDisabled = false,
	hiddenNicknameField = false,
	formLabel = null,
	isTwoColumns = false,
	colonyDisabled = false,
	cityDisabled = false,
	stateDisabled = false,
	countries = [],
	countryDisabled = true,
	isMainAddress = false,
	addressType,
	isNotBilling = false,
	selectMenuSx,
}) => {
	const addressFormNLS = useLocalization('AddressForm');
	const localization = useLocalization('AddressCard');
	const { isNoExteriorNumberChecked, handleNoExteriorNumberChange } = useNoExteriorNumber({
		exteriorNumber: address.addressLine2,
		onNamedValueChange,
		setAddressErrors,
		setIsEdited,
	});

	const handleAlphaNumericInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const isValid = new RegExp(REGEX.SPANISH_ALPHA_NUMERIC).test(event.target.value);
		if (!isValid && event.target.value.trim() !== '') {
			return;
		}
		handleChange(event);
	};

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		handleInputChange(event);
		setIsEdited(true);

		if (event.target.name === 'addressLine1') {
			setAddressErrors((prevState) => ({
				...prevState,
				street: false,
			}));
		}

		if (event.target.name === 'addressLine2') {
			setAddressErrors((prevState) => ({
				...prevState,
				exteriorNumber: false,
			}));
		}

		if (event.target.name === 'addressLine3Txt') {
			setAddressErrors((prevState) => ({
				...prevState,
				colonyNotFound: false,
			}));
		}
	};

	const handleNickNameInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		handleInputChange(event);
		setIsEdited(true);
		const nickNameValue = event.target.value;
		validateNickName(nickNameValue);
	};

	const noErrorColony = addressErrors.zipCode && isNotBilling;

	const selectMenuProps = selectMenuSx ? { sx: selectMenuSx } : undefined;

	const handleZipCodePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
		onPasteNumeric(e);
		cleanFieldsPaste();
	};

	return (
		<Stack>
			{formLabel}
			<Grid item container columnSpacing={1.25} rowSpacing={1.875}>
				{/* Alias textfield */}
				{!hiddenNicknameField ? (
					<Grid item xs={12} order={{ xs: 1, lg: 0 }}>
						<TextField
							data-testid="address-form-nickName"
							id="address-form-nickName"
							name="nickName"
							size="small"
							label={addressFormNLS.Labels.NickName.t()}
							onChange={handleNickNameInputChange}
							onBlur={() => {
								handleInputChange({
									target: { name: 'nickName', value: address.nickName.trim() },
								} as React.ChangeEvent<HTMLInputElement>);
								validateNickName(address.nickName);
							}}
							value={isMainAddress ? localization.AccountAddress.t() : address.nickName}
							inputProps={addressFormSchema.nickName}
							fullWidth
							autoComplete="nickname"
							error={addressErrors.nickName}
							disabled={nickNameDisabled}
							helperText={helperText.nickName}
							sx={AAFieldItemSX}
						/>
					</Grid>
				) : null}

				{/* First name textfield */}
				{addressType === 'Billing' ? (
					<Grid item xs={12} md={isTwoColumns ? 6 : 12} order={{ xs: 2, lg: 0 }}>
						<TextField
							id="address-form-first-name"
							data-testid="address-form-first-name"
							name="firstName"
							size="small"
							label={addressFormNLS.Labels.FirstName.t()}
							onChange={handleAlphaNumericInputChange}
							onBlur={() =>
								setAddressErrors((prevState) => ({
									...prevState,
									firstName: address.firstName.trim() === EMPTY_STRING,
								}))
							}
							value={address.firstName}
							inputProps={addressFormSchema.firstName}
							fullWidth
							autoComplete="first-name"
							error={addressErrors.firstName}
							helperText={helperText.firstName}
							sx={AAFieldItemSX}
						/>
					</Grid>
				) : null}

				{/* Last name textfield */}
				{addressType === 'Billing' ? (
					<Grid item xs={12} md={isTwoColumns ? 6 : 12} order={{ xs: 3, lg: 0 }}>
						<TextField
							id="address-form-last-name"
							data-testid="address-form-last-name"
							name="lastName"
							size="small"
							label={addressFormNLS.Labels.BillingLastName.t()}
							onChange={handleAlphaNumericInputChange}
							onBlur={() =>
								setAddressErrors((prevState) => ({
									...prevState,
									lastName: address.lastName.trim() === EMPTY_STRING,
								}))
							}
							value={address.lastName}
							inputProps={addressFormSchema.lastName}
							fullWidth
							autoComplete="last-name"
							error={addressErrors.lastName}
							helperText={helperText.lastName}
							sx={AAFieldItemSX}
						/>
					</Grid>
				) : null}

				{/* Street textfield */}
				<Grid
					item
					xs={12}
					md={addressType === 'Billing' ? (isTwoColumns ? 6 : 12) : 12}
					order={{ xs: 4, lg: 0 }}
				>
					<TextField
						id="address-form-address-line1"
						data-testid="address-form-address-line1"
						name="addressLine1"
						size="small"
						label={addressFormNLS.Labels.Street.t()}
						onChange={handleAlphaNumericInputChange}
						onBlur={() =>
							setAddressErrors((prevState) => ({
								...prevState,
								street: address.addressLine1.trim() === EMPTY_STRING,
							}))
						}
						value={address.addressLine1}
						inputProps={addressFormSchema.street}
						fullWidth
						error={addressErrors.street}
						autoComplete="address-street"
						helperText={helperText.street}
						sx={AAFieldItemSX}
					/>
				</Grid>

				{/* Exterior number textfield */}
				<Grid item xs={12} md={isTwoColumns ? 6 : 12} order={{ xs: 5, lg: 0 }} position="relative">
					<Stack spacing={1.25}>
						<ExteriorNumberField
							value={address.addressLine2}
							isNoExteriorNumberChecked={isNoExteriorNumberChecked}
							error={addressErrors.exteriorNumber}
							helperText={helperText.exteriorNumber}
							onChange={handleChange}
							onBlur={() =>
								setAddressErrors((prevState) => ({
									...prevState,
									exteriorNumber: address.addressLine2.trim() === EMPTY_STRING,
								}))
							}
						/>
						<NoExteriorNumberCheckbox
							checked={isNoExteriorNumberChecked}
							onChange={handleNoExteriorNumberChange}
							sx={isTwoColumns ? AANoExteriorNumberTwoColumnsSX : undefined}
						/>
					</Stack>
				</Grid>

				{/* Address indications textfield */}
				<Grid item xs={12} md={isTwoColumns ? 6 : 12} order={{ xs: 6, lg: 0 }}>
					<TextField
						data-testid="address-form-indications"
						id={ADDRESS_FORM_IDS.interiorNumberInput}
						name="addressField3"
						size="small"
						label={addressFormNLS.Labels.Directions.t()}
						onChange={handleAlphaNumericInputChange}
						value={address.addressField3}
						inputProps={addressFormSchema.addressIndications}
						fullWidth
						autoComplete="address-indications"
						sx={AAFieldItemSX}
					/>
				</Grid>

				{/* Country select */}
				{addressType?.includes('Billing') && countries.length > 0 ? (
					<Grid item xs={12} md={isTwoColumns ? 6 : 12} order={{ xs: 8, lg: 0 }}>
						<FormControl fullWidth size="small" sx={AAFieldItemSX}>
							<InputLabel id="address-form-country-label">
								{addressFormNLS.Labels.Country.t()}
							</InputLabel>
							<Select
								labelId="address-form-country-label"
								id="address-form-country"
								name="country"
								value={address.country || INIT}
								label={addressFormNLS.Labels.Country.t()}
								IconComponent={ChevronRight}
								sx={AAFormSelectSX}
								onChange={(e) => {
									handleSelectChange(e);
									setIsEdited(true);
								}}
								placeholder={addressFormNLS.Labels.SelectCountry.t()}
								disabled={countryDisabled}
								MenuProps={selectMenuProps}
							>
								{countries.map((country: string) => (
									<MenuItem key={country} value={country} disabled={country === INIT}>
										{country === INIT
											? addressFormNLS.Labels.SelectCountry.t()
											: COUNTRY_LABELS[country] ?? country}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>
				) : null}

				{/* Zipcode textfield */}
				<Grid item xs={12} md={isTwoColumns ? 6 : 12} order={{ xs: 7, lg: 0 }}>
					<TextField
						id="address-form-zipCode"
						data-testid="address-form-zipCode"
						name="zipCode"
						size="small"
						label={addressFormNLS.Labels.ZipCode.t()}
						onChange={handleZipCodeInputChange}
						onBlur={() => {
							if (address.zipCode.length > 0 && address.zipCode.length < 5) {
								setAddressErrors((prevState) => ({
									...prevState,
									zipCode: true,
								}));
							} else if (address.zipCode.length === 0) {
								setAddressErrors((prevState) => ({
									...prevState,
									zipCode: true,
								}));
							}
						}}
						error={addressErrors.zipCode}
						value={address.zipCode}
						onKeyDown={validateNumericInput}
						inputProps={addressFormSchema.zipCode}
						fullWidth
						autoComplete="postal-code"
						helperText={helperText.zipCode}
						sx={AAFieldItemSX}
						onBeforeInput={onBeforeInputNumeric}
						onPaste={handleZipCodePaste}
						onDrop={onDropNumeric}
						inputMode="numeric"
					/>
				</Grid>

				{/* Colony select */}
				{showInputStateAndCity ? null : (
					<Grid item xs={12} md={isTwoColumns ? 6 : 12} order={{ xs: 9, lg: 0 }}>
						<FormControl
							fullWidth
							size="small"
							required={noErrorColony ? false : addressFormSchema.colony.required}
							error={noErrorColony ? false : addressErrors.colony}
							sx={AAFieldItemSX}
						>
							<InputLabel id="address-form-colony-label" sx={{ pointerEvents: 'none' }}>
								{addressFormNLS.Labels.Colony.t()}
							</InputLabel>
							<Select
								labelId="address-form-colony-label"
								id="address-form-colony"
								name="addressLine3"
								value={address.addressLine3 ?? ''}
								label={addressFormNLS.Labels.Colony.t()}
								IconComponent={ChevronRight}
								sx={AAFormSelectSX}
								inputProps={addressFormSchema.colony}
								autoComplete="address-colony"
								onChange={(e) => {
									handleColonySelectChange(e);
									setIsEdited(true);
								}}
								placeholder={addressFormNLS.Labels.SelectColony.t()}
								disabled={colonyDisabled}
								MenuProps={selectMenuProps}
							>
								{colonies.map((colony: string) => (
									<MenuItem
										key={colony}
										value={colony === INIT ? '0' : colony}
										disabled={colony === INIT}
									>
										{colony === INIT ? addressFormNLS.Labels.SelectColony.t() : colony}
									</MenuItem>
								))}
							</Select>
							{addressErrors.colony && !noErrorColony && (
								<FormHelperText>{helperText.colony}</FormHelperText>
							)}
						</FormControl>
					</Grid>
				)}

				{/* Colony not found textfield */}
				{address.addressLine3 === OTHER_COLONY || showInputStateAndCity ? (
					<Grid item xs={12} order={{ xs: 10, lg: 0 }}>
						<TextField
							data-testid="address-form-colony-not-found"
							id="address-form-colony-not-found"
							name="addressLine3Txt"
							size="small"
							label={addressFormNLS.Labels.ColonyName.t()}
							onChange={handleChange}
							onBlur={() =>
								setAddressErrors((prevState) => ({
									...prevState,
									colonyNotFound: (address.addressLine3Txt ?? '').trim() === EMPTY_STRING,
								}))
							}
							error={addressErrors.colonyNotFound}
							value={address.addressLine3Txt}
							fullWidth
							helperText={helperText.colonyNotFound}
							sx={AAFieldItemSX}
							InputProps={{
								...addressFormSchema.colonyNotFound,
								endAdornment: (
									<FieldTooltip content={addressFormNLS.Msgs.ColonyNotFoundTooltip.t()} />
								),
							}}
						/>
					</Grid>
				) : null}

				{/* Municipality section */}
				<Grid item xs={12} md={isTwoColumns ? 6 : 12} order={{ xs: 11, lg: 0 }}>
					{!showInputStateAndCity && !showInputCity ? (
						<FormControl
							fullWidth
							size="small"
							error={addressErrors.municipality}
							sx={AAFieldItemSX}
							required={addressFormSchema.municipality.required}
						>
							<InputLabel id="address-form-municipality-label">
								{addressFormNLS.Labels.Municipality.t()}
							</InputLabel>
							<Select
								labelId="address-form-municipality-label"
								id="address-form-municipality"
								name="city"
								value={address.city ?? ''}
								label={addressFormNLS.Labels.Municipality.t()}
								error={addressErrors.municipality}
								IconComponent={ChevronRight}
								sx={AAFormSelectSX}
								onChange={(e) => {
									handleMunicipalitySelectChange(e);
									setIsEdited(true);
								}}
								disabled={cityDisabled}
								MenuProps={selectMenuProps}
								inputProps={addressFormSchema.municipality}
							>
								{cities.map((city: string) => (
									<MenuItem key={city} value={city === INIT ? '0' : city} disabled={city === INIT}>
										{city === INIT ? addressFormNLS.Labels.SelectMunicipality.t() : city}
									</MenuItem>
								))}
							</Select>
							{addressErrors.municipality && (
								<FormHelperText>{helperText.municipality}</FormHelperText>
							)}
						</FormControl>
					) : (
						<TextField
							data-testid="address-form-municipality"
							id="address-form-municipality"
							name="city"
							size="small"
							label={addressFormNLS.Labels.Municipality.t()}
							onChange={handleChange}
							onBlur={() =>
								setAddressErrors((prevState) => ({
									...prevState,
									municipality: (address.city ?? '').trim() === EMPTY_STRING,
								}))
							}
							error={addressErrors.municipality}
							value={address.city}
							fullWidth
							helperText={helperText.municipality}
							sx={AAFieldItemSX}
							inputProps={addressFormSchema.municipality}
						/>
					)}
				</Grid>

				{/* State section */}
				<Grid item xs={12} md={isTwoColumns ? 6 : 12} order={{ xs: 12, lg: 0 }}>
					{showInputStateAndCity ? (
						<TextField
							data-testid="address-form-state"
							id="address-form-state"
							name="state"
							size="small"
							label={addressFormNLS.Labels.State.t()}
							onChange={handleChange}
							onBlur={() =>
								setAddressErrors((prevState) => ({
									...prevState,
									state: (address.state ?? '').trim() === EMPTY_STRING,
								}))
							}
							error={addressErrors.state}
							value={address.state}
							fullWidth
							helperText={helperText.state}
							sx={AAFieldItemSX}
						/>
					) : (
						<FormControl fullWidth size="small" error={error.state} sx={AAFieldItemSX}>
							<InputLabel id="address-form-state-label">
								{addressFormNLS.Labels.State.t()}
							</InputLabel>
							<Select
								labelId="address-form-state-label"
								id="address-form-state"
								name="state"
								value={address.state ?? ''}
								label={addressFormNLS.Labels.State.t()}
								error={error.state}
								IconComponent={ChevronRight}
								sx={AAFormSelectSX}
								onChange={(e) => {
									handleSelectChange(e);
									setIsEdited(true);
								}}
								disabled={stateDisabled}
								MenuProps={selectMenuProps}
							>
								{states.map((state: string) => (
									<MenuItem key={state} value={state === INIT ? '0' : state}>
										{state === INIT ? addressFormNLS.Labels.SelectState.t() : state}
									</MenuItem>
								))}
							</Select>
							{error.state && <FormHelperText>{helperText.state}</FormHelperText>}
						</FormControl>
					)}
				</Grid>
			</Grid>
		</Stack>
	);
};
